import * as React from 'react'
import { AccountingSyncBadge } from '@/components/AccountingSyncBadge'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { ContactFinancialPayment } from '@/lib/contacts/types'
import { displayDate } from '@/lib/dates'
import { EmptyState } from '@/components/ui/empty-state'
import { OwnedChip } from '@/components/OwnedChip'

export function ContactPaymentsHistory({ payments }: { payments: ContactFinancialPayment[] }) {
  const [paymentType, setPaymentType] = React.useState<string | null>(null)
  const paymentTypes = Array.from(new Set(payments.map((payment) => payment.method)))
  const filteredPayments =
    paymentType === null ? payments : payments.filter((payment) => payment.method === paymentType)

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-body font-semibold text-foreground">Payments</h3>
        {paymentTypes.length > 0 && (
          <Select
            value={paymentType === null ? 'all' : `payment-type-${paymentType}`}
            onValueChange={(value) =>
              setPaymentType(value === 'all' ? null : value.slice('payment-type-'.length))
            }
          >
            <SelectTrigger aria-label="Filter Payments by Payment Type" className="h-8 w-44">
              <SelectValue placeholder="All Payment Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payment Types</SelectItem>
              {paymentTypes.map((type) => (
                <SelectItem key={type} value={`payment-type-${type}`}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {payments.length === 0 ? (
        <EmptyState title="No Payments recorded" className="items-start px-0 text-left" />
      ) : filteredPayments.length === 0 ? (
        <EmptyState
          title="No Payments match this Payment Type"
          className="items-start px-0 text-left"
        />
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Payment</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Accounting</TableHead>
                <TableHead className="text-right">Settled</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.map((payment) => (
                <TableRow id={`payment-${payment.id}`} key={payment.id}>
                  <TableCell>
                    <span className="flex items-center gap-2 font-medium text-foreground">
                      {payment.amount}
                      <OwnedChip owner={payment.owner} />
                    </span>
                    <span className="block text-caption text-muted-foreground">
                      {displayDate(payment.paid_on)}
                    </span>
                  </TableCell>
                  <TableCell>Invoice #{payment.invoice_number}</TableCell>
                  <TableCell className="text-muted-foreground">{payment.method}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {payment.reference || '-'}
                  </TableCell>
                  <TableCell>{chargebackBadge(payment)}</TableCell>
                  <TableCell>
                    <AccountingSyncBadge
                      state={payment.accounting_sync_state ?? null}
                      detail={payment.accounting_sync_detail}
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{payment.settled}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  )
}

function chargebackBadge(payment: ContactFinancialPayment) {
  if (payment.disputed) return <Badge variant="destructive">Disputed</Badge>
  if (payment.chargeback_shortfall_cents && payment.chargeback_shortfall)
    return (
      <Badge variant="destructive">
        Chargeback lost · {payment.chargeback_shortfall} shortfall
      </Badge>
    )
  if (payment.chargeback_outcome === 'lost') return <Badge variant="neutral">Chargeback lost</Badge>
  if (payment.chargeback_outcome === 'won') return <Badge variant="neutral">Chargeback won</Badge>
  return <span className="text-muted-foreground">-</span>
}
